import React from "react";
import { Phone, MessageCircle, Bot } from "lucide-react";
import { telLink, waLink, trackCallEvent, trackWhatsAppEvent } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { OPEN_CHAT_EVENT } from "@/lib/chatEvents";

/**
 * Mobile-only bottom action bar (hidden on md+).
 * Three actions: CALL (primary, widest), WhatsApp, AI Chat (compact).
 * The chat button dispatches a global event that `LazyChatLauncher` listens
 * to, so the heavy chat widget stays lazy-loaded while the launcher button
 * itself is no longer floating over the content on small screens.
 */
export function StickyCTA() {
  const { t } = useLang();

  const openChat = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT));
    }
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-[hsl(var(--border))] bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85 md:hidden safe-bottom"
      data-testid="sticky-cta-bar"
      role="region"
      aria-label="Emergency contact bar"
    >
      <div className="max-w-[1120px] mx-auto px-3 py-2.5 flex items-center gap-2">
        <a
          href={telLink()}
          onClick={trackCallEvent}
          className="flex-[1.3] min-w-0 inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-[hsl(var(--brand-aqua))] text-white font-semibold press shadow-sm"
          data-testid="sticky-cta-call-button"
        >
          <Phone className="h-5 w-5 shrink-0" />
          <span className="truncate">{t("common.call_now_short")}</span>
        </a>
        <a
          href={waLink()}
          onClick={trackWhatsAppEvent}
          target="_blank"
          rel="noreferrer"
          className="flex-1 min-w-0 inline-flex items-center justify-center gap-2 h-12 rounded-xl border border-[hsl(var(--border))] bg-white text-[hsl(var(--brand-navy))] font-semibold press shadow-sm"
          data-testid="sticky-cta-whatsapp-button"
        >
          <MessageCircle className="h-5 w-5 text-[hsl(var(--success))] shrink-0" />
          <span className="truncate">{t("nav.whatsapp")}</span>
        </a>
        <button
          type="button"
          onClick={openChat}
          className="shrink-0 inline-flex items-center justify-center h-12 w-12 rounded-xl border border-[hsl(var(--brand-aqua))]/30 bg-[hsl(var(--accent))] text-[hsl(var(--brand-aqua))] press shadow-sm"
          data-testid="sticky-cta-chat-button"
          aria-label={t("chat.title")}
        >
          <Bot className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

export default StickyCTA;
